import { ShieldCheck, Award, Users, HeartHandshake, MapPin, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import PageLayout from "@/components/PageLayout";
import PageHero from "@/components/sections/PageHero";
import CTABanner from "@/components/sections/CTABanner";
import { canonicalFor } from "@/lib/site";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Do It Right the First Time",
    desc: "Every roof we touch follows the same checklist — full tear-off, ice & water shield, synthetic underlayment, proper ventilation. We don't ship shortcuts.",
  },
  {
    icon: Award,
    title: "Craftsmanship is Specific",
    desc: "Our crews are trained on each manufacturer's spec. Certified installer status with GAF, IKO, and Malarkey. Every penetration sealed by hand.",
  },
  {
    icon: Users,
    title: "Treat the Home Like Ours",
    desc: "Plywood over the garden beds. Magnetic nail sweep three passes. Daily cleanup. Your lawn looks better when we leave than when we arrived.",
  },
  {
    icon: HeartHandshake,
    title: "Honest Pricing, Honest Timelines",
    desc: "Line-item proposals. No commission salespeople. The price you sign is the price you pay — every quote includes a written workmanship warranty.",
  },
];

const STATS = [
  { value: "15+", label: "Years in the trade" },
  { value: "2,000+", label: "Roofs completed" },
  { value: "$5M", label: "Liability coverage" },
  { value: "10 yr", label: "Workmanship warranty" },
];

const TEAM = [
  {
    role: "Founder & Lead Estimator",
    name: "Marcus Crestline",
    bio: "Third-generation roofer. Climbs every estimate himself. Hates corner-cutting more than anyone you'll meet.",
  },
  {
    role: "Operations Manager",
    name: "Sarah Whitmore",
    bio: "Runs the schedule, the parts pipeline, and the magnetic-sweep audits. The reason your project finishes on time.",
  },
  {
    role: "Senior Installation Crew Lead",
    name: "Daniel Park",
    bio: "12 years on Crestline crews. GAF Master Elite & IKO ShieldPro certified. Trains every new installer himself.",
  },
  {
    role: "Field Service & Repair Lead",
    name: "Connor McAllister",
    bio: "Diagnoses leaks two other roofers couldn't trace. Carries an infrared camera and the patience to use it.",
  },
];

const CERTIFICATIONS = [
  "GAF Master Elite Certified",
  "IKO ShieldPro Installer",
  "Malarkey Emerald Pro",
  "WCB Coverage — C-44829",
  "BC Roofing Contractors Association",
  "BBB Accredited — A+",
];

const SERVICE_AREAS = [
  "Vancouver",
  "Burnaby",
  "North Vancouver",
  "West Vancouver",
  "Richmond",
  "Surrey",
  "Coquitlam",
  "Port Moody",
  "New Westminster",
  "Delta",
  "Langley",
  "Maple Ridge",
  "Pitt Meadows",
  "Abbotsford",
  "Mission",
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About | Crestline Roofing Ltd."
        description="Crestline Roofing Ltd. is a Vancouver-based roofing company serving the Fraser Valley. Learn about our team, our values, and our commitment to quality."
        canonical={canonicalFor("/about")}
      />
      <PageLayout>
        {({ openEstimate }) => (
          <main data-testid="about-page" className="bg-[var(--crest-charcoal)] text-white">
            <PageHero
              eyebrow="About Crestline"
              title="About Crestline Roofing Ltd."
              subtitle="Founded in Burnaby in 2010, Crestline Roofing Ltd. has installed and repaired more than 2,000 roofs across the Lower Mainland. We're the team you call when the last guys took shortcuts."
              image="https://images.unsplash.com/photo-1601658797866-7ae55d05a817?crop=entropy&cs=srgb&fm=webp&q=85&w=2400"
              imageAlt="Residential rooftops across the Lower Mainland — Crestline Roofing Ltd. Vancouver"
              breadcrumb={[{ label: "Home", href: "/" }, { label: "About" }]}
            />

            {/* Story */}
            <section
              data-testid="about-story"
              className="relative bg-[var(--crest-cream)] text-[#121212] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                  <div className="lg:col-span-7 reveal">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="h-px w-12 bg-[var(--crest-amber)]" />
                      <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                        Sixteen years on the roof
                      </span>
                    </div>
                    <h2
                      data-testid="about-story-heading"
                      className="font-display uppercase font-extrabold leading-[0.92] tracking-tight text-[#121212]"
                      style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}
                    >
                      Our Story.
                    </h2>
                    <div className="mt-8 space-y-5 font-body text-base md:text-lg text-[#3a3a3a] leading-relaxed max-w-2xl">
                      <p>
                        In 2010, our founder — a third-generation roofer — kept getting called out to homes that should have had decades left. Underlayment skipped. Flashing nailed instead of sealed. Ventilation cooked into the attic. The owners had paid for a real roof and gotten a costume.
                      </p>
                      <p>
                        Crestline was built to be the opposite of that. Manufacturer-certified crews. Full tear-off, every time. Written warranties, every time. No commissioned salespeople, ever.
                      </p>
                      <p>
                        Sixteen years and two thousand roofs later, the standard hasn&apos;t moved an inch. We still climb every roof before quoting. We still photograph every penetration before closing the deck. We still hand-seal flashing because nail guns don&apos;t know the difference between asphalt and copper. The roof is the only thing standing between your family and the weather. We treat it that way.
                      </p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="lg:col-span-5 grid grid-cols-2 gap-5">
                    {STATS.map((s, i) => (
                      <div
                        key={s.label}
                        data-testid={`about-stat-${i}`}
                        className="reveal bg-[#121212] text-white p-7"
                        style={{ transitionDelay: `${i * 80}ms` }}
                      >
                        <div className="font-display font-extrabold text-4xl md:text-5xl text-[var(--crest-amber)] leading-none mb-3">
                          {s.value}
                        </div>
                        <div className="font-body text-xs uppercase tracking-[0.25em] text-white/60">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Team */}
            <section
              data-testid="about-team"
              className="relative shingle-texture bg-[var(--crest-slate)] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl mb-16">
                  <div className="reveal flex items-center gap-3 mb-6">
                    <span className="h-px w-12 bg-[var(--crest-amber)]" />
                    <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                      The People Behind the Roof
                    </span>
                  </div>
                  <h2
                    data-testid="about-team-heading"
                    className="reveal font-display uppercase font-extrabold text-white leading-[0.95] tracking-tight"
                    style={{ fontSize: "clamp(2rem, 4.8vw, 3.8rem)" }}
                  >
                    Our Team.
                  </h2>
                  <p className="reveal font-body text-base md:text-lg text-white/65 leading-relaxed mt-6 max-w-2xl">
                    No high-pressure sales reps. No revolving-door subcontractors. Just a small, certified team that&apos;s been on Crestline roofs for years.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {TEAM.map((t, i) => (
                    <article
                      key={t.name}
                      data-testid={`about-team-${i}`}
                      className="reveal card-lift bg-[#141d2e] border border-white/10 border-t-2 border-t-[var(--crest-amber)] p-7 md:p-8"
                      style={{ transitionDelay: `${i * 80}ms` }}
                    >
                      <div className="font-display uppercase tracking-[0.3em] text-[10px] font-semibold text-[var(--crest-amber)] mb-3">
                        {t.role}
                      </div>
                      <h3 className="font-display uppercase font-bold text-xl md:text-2xl text-white tracking-tight mb-3">
                        {t.name}
                      </h3>
                      <p className="font-body text-sm md:text-[15px] text-white/65 leading-relaxed">{t.bio}</p>
                    </article>
                  ))}
                </div>
              </div>
            </section>

            {/* Values (h3) */}
            <section
              data-testid="about-values"
              className="relative bg-[var(--crest-charcoal)] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl mb-16">
                  <div className="reveal flex items-center gap-3 mb-6">
                    <span className="h-px w-12 bg-[var(--crest-amber)]" />
                    <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                      What We Stand For
                    </span>
                  </div>
                  <h3
                    className="reveal font-display uppercase font-bold text-white leading-[0.95] tracking-tight"
                    style={{ fontSize: "clamp(1.8rem, 3.6vw, 2.8rem)" }}
                  >
                    Four values. No exceptions.
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {VALUES.map((v, i) => {
                    const Icon = v.icon;
                    return (
                      <article
                        key={v.title}
                        data-testid={`about-value-${i}`}
                        className="reveal card-lift bg-[#161616] border border-white/10 border-t-2 border-t-[var(--crest-amber)] p-7 md:p-9"
                        style={{ transitionDelay: `${i * 80}ms` }}
                      >
                        <Icon size={32} strokeWidth={1.5} className="text-[var(--crest-amber)] mb-5" />
                        <h4 className="font-display uppercase font-bold text-xl md:text-2xl text-white tracking-tight mb-4">
                          {v.title}
                        </h4>
                        <p className="font-body text-sm md:text-[15px] text-white/65 leading-relaxed">
                          {v.desc}
                        </p>
                      </article>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Service area */}
            <section
              data-testid="about-service-area"
              className="relative bg-[#0d1320] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                  <div className="lg:col-span-5">
                    <div className="reveal flex items-center gap-3 mb-6">
                      <span className="h-px w-12 bg-[var(--crest-amber)]" />
                      <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                        Where We Work
                      </span>
                    </div>
                    <h2
                      data-testid="about-area-heading"
                      className="reveal font-display uppercase font-extrabold text-white leading-[0.95] tracking-tight"
                      style={{ fontSize: "clamp(2rem, 4.5vw, 3.6rem)" }}
                    >
                      Our Service Area.
                    </h2>
                    <p className="reveal font-body text-base md:text-lg text-white/65 leading-relaxed mt-6 max-w-md">
                      Local crews dispatched across the Lower Mainland and Fraser Valley. From the North Shore to Mission, every roof gets the same standard.
                    </p>
                    <a
                      href="tel:+17789176817"
                      data-testid="about-area-phone"
                      className="reveal inline-flex items-center gap-3 mt-8 font-display uppercase tracking-wider font-bold text-sm px-7 py-4 border border-[var(--crest-amber)] text-[var(--crest-amber)] hover:bg-[var(--crest-amber)] hover:text-[#121212] transition-colors"
                    >
                      <Phone size={16} />
                      Call (778) 917-6817
                    </a>
                  </div>
                  <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {SERVICE_AREAS.map((c, i) => (
                      <div
                        key={c}
                        data-testid={`about-area-${i}`}
                        className="reveal flex items-center gap-3 p-4 bg-[#161616] border border-white/10"
                        style={{ transitionDelay: `${i * 30}ms` }}
                      >
                        <MapPin size={12} className="text-[var(--crest-amber)] flex-shrink-0" />
                        <span className="font-display uppercase tracking-[0.15em] text-xs font-semibold text-white/85">
                          {c}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Certifications (h3) */}
            <section
              data-testid="about-certifications"
              className="relative bg-[var(--crest-charcoal)] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="reveal flex items-center gap-3 mb-6">
                  <span className="h-px w-12 bg-[var(--crest-amber)]" />
                  <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                    Certifications &amp; Insurance
                  </span>
                </div>
                <h3
                  className="reveal font-display uppercase font-bold text-white leading-[0.95] tracking-tight max-w-3xl mb-12"
                  style={{ fontSize: "clamp(1.8rem, 3.6vw, 2.8rem)" }}
                >
                  Licensed, certified, and properly insured.
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {CERTIFICATIONS.map((c, i) => (
                    <div
                      key={c}
                      data-testid={`about-cert-${i}`}
                      className="reveal flex items-center gap-4 p-5 border border-white/10 bg-[#161616]"
                      style={{ transitionDelay: `${i * 60}ms` }}
                    >
                      <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center border border-[var(--crest-amber)]/40 text-[var(--crest-amber)]">
                        <ShieldCheck size={16} strokeWidth={1.8} />
                      </span>
                      <span className="font-display uppercase tracking-[0.15em] text-sm font-semibold text-white leading-tight">
                        {c}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <CTABanner
              onOpenEstimate={openEstimate}
              eyebrow="Work with Crestline"
              heading="Ready to talk roofing?"
              headingTag="h3"
              subheading="48-hour estimate. No commission games."
              buttonLabel="Request Your Free Estimate"
            />
          </main>
        )}
      </PageLayout>
    </>
  );
}
